import React, { Fragment } from 'react';
import _ from 'lodash';
import loadable from '@loadable/component';
import styled, { ThemeProvider, css } from 'styled-components';
import { responsiveText } from 'utilities/style';

import { withTheme } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';

import ContentTypography from 'components/ContentTypography';

const Typed = loadable(() => import('components/Typed'));

const HeaderWrapper = styled.div`
  margin: 10vh 0;
  position: relative;
  z-index: 1;
`;

const BoldTitleTypography = styled(Typography)`
  @media (max-width: 640px) {
    font-size: calc(12px + 4 * ((100vw - 320px) / 680));
  }
  @media (min-width: 1000px) {
    font-size: 18px;
  }
`;

const BoldLeadTypography = styled(Typography)`
  ${props =>
    props.gradient &&
    css`
      @supports (-webkit-background-clip: text) and
        (-webkit-text-fill-color: transparent) {
        background: linear-gradient(
          ${props => props.theme?.brand?.gradient?.primary}
        );
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        span {
          background: linear-gradient(
            ${props => props.theme?.brand?.gradient?.primary}
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      }
    `}

  ${responsiveText(48, 100, 0)}
  letter-spacing:-0.03em;
  padding-bottom: 10px;
`;

const SubheadingTypography = styled(ContentTypography)`
  font-weight: 400;
  font-size: 24px;
  line-height: 1.3em;
  ${props =>
    props.size === 'jumbo' &&
    css`
      font-weight: 300;
      font-size: 32px;
    `};
  @media (max-width: 640px) {
    font-size: calc(18px + 14 * ((100vw - 320px) / 680));
  }
`;

const LandingHeader = ({
  heading,
  subheading,
  typed_suffixes,
  overline,
  use_rich_text_heading,
  rich_heading,
  use_rich_text_subheading,
  rich_subheading,
  legacy_subheading,
  style = 'default',
  gradient_type,
  theme,
  ctas,
  ...other
}) => {
  const HeadingText = () => (
    <>
      {heading}{' '}
      {typed_suffixes?.length > 0 && (
        <Typed
          strings={typed_suffixes}
          minWidth={`${_.maxBy(typed_suffixes, string => string.length)
            ?.length / 2}em`}
          underlineColor={`linear-gradient(${theme?.brand?.gradient?.primary})`}
        />
      )}
    </>
  );
  return (
    <ThemeProvider theme={theme}>
      <HeaderWrapper {...other}>
        <Fragment>
          {overline && (
            <>
              {style === 'default' ? (
                <Typography variant="overline" gutterBottom>
                  {overline}
                </Typography>
              ) : (
                <BoldTitleTypography variant="overline" gutterBottom>
                  {overline}
                </BoldTitleTypography>
              )}
            </>
          )}
          {heading && !use_rich_text_heading && (
            <>
              {style === 'default' ? (
                <Typography variant="h1" gutterBottom>
                  <HeadingText />
                </Typography>
              ) : (
                <BoldLeadTypography
                  variant="h1"
                  gradient={style === 'bold_gradient'}
                  gutterBottom
                >
                  <HeadingText />
                </BoldLeadTypography>
              )}
            </>
          )}
          {use_rich_text_heading && (
            <ContentTypography
              gutterBottom
              anchorOverrideColor={
                theme && theme.palette.type === 'dark' ? '#ffffff' : null
              }
              theme={theme}
              component="p"
              dangerouslySetInnerHTML={{
                __html: rich_heading
              }}
            />
          )}
          {subheading && !use_rich_text_subheading && (
            <SubheadingTypography variant="h3" component="p">
              {subheading}
            </SubheadingTypography>
          )}
          {(use_rich_text_subheading || legacy_subheading) && (
            <SubheadingTypography
              gutterBottom
              anchorOverrideColor={
                theme && theme.palette.type === 'dark' ? '#ffffff' : null
              }
              theme={theme}
              component="p"
              variant="h3"
              dangerouslySetInnerHTML={{
                __html: legacy_subheading ? legacy_subheading : rich_subheading
              }}
            />
          )}
          {ctas && ctas.length > 0 && (
            <>
              {ctas.map((cta, idx) => (
                <Button href={cta.href} size="large" variant="contained">
                  {cta.title}
                </Button>
              ))}
            </>
          )}
        </Fragment>
      </HeaderWrapper>
    </ThemeProvider>
  );
};

export default withTheme()(LandingHeader);
